import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import {
  Box,
  Select,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Avatar,
  Button,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Text,
} from "@skailer-com/uikit";
import useFormatter, { getNameInitials } from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import { useEmployeeWorkflowRun } from "@/queries/employees/use-employee-workflow.query.ts";
import { useWorkflowSummary, useWorkflowsSearch } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { getApiWorkflowTriggerTypeLabel } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowFilter } from "@/types/workflows/workflow.types.ts";
import { ComponentProps, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "react-use";
import { Controller, useForm } from "react-hook-form";
import { WorkflowTriggerCaption } from "@/pages/workflows/components/WorkflowTriggerCaption";

function WorkflowSummaryRow({
  leading,
  title,
  caption,
  captionSlot,
}: {
  leading: ReactNode;
  title: string;
  caption?: string;
  captionSlot?: ReactNode;
}) {
  return (
    <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
      {leading}
      <Stack gap={"xs"} className={"min-w-0"}>
        <Text className={"font-semibold"}>{title}</Text>
        {caption && <Text className={"text-secondary"}>{caption}</Text>}
        {captionSlot}
      </Stack>
    </Stack>
  );
}

export interface Props extends ModalProps {
  employeeId: number;
}

export function WorkflowAssignToEmployeeModal({ ...props }: Props) {
  const { employeeId, id } = props;
  const { close } = useModals();
  const { showInfo } = useToasts();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const library = filtersLibrary();
  const { canIEdit } = usePermissions();
  const { getWorkflowTriggerTypeIcon, getWorkflowTriggerDescription } = useWorkflows();
  const { renderEmployeeName, renderTimeAgo } = useFormatter();
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | undefined>(undefined);

  const runWorkflow = useEmployeeWorkflowRun();
  const { data: summary, isLoading: isSummaryLoading } = useWorkflowSummary(
    selectedWorkflowId,
    !!selectedWorkflowId
  );

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiWorkflowFilter>({
    filters: [library.page_number, library.text],
    source: "Workflow Assign To Employee Modal",
  });

  const { data: workflows, isFetching } = useWorkflowsSearch({ filter: filter });

  const { control } = useForm<{ workflowId: number | undefined }>({
    defaultValues: { workflowId: undefined },
  });

  const [searchTerm, setSearchTerm] = useState("");
  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const selectOptions: ComponentProps<typeof Select>["options"] =
    workflows?.items.map((workflow) => {
      return { id: workflow.id, title: workflow.title, caption: workflow.folder?.title };
    }) ?? [];
  const selectedWorkflow = workflows?.items.find((workflow) => workflow.id === selectedWorkflowId);
  const canOpenWorkflow = canIEdit(ApiPermission.workflow_module);
  const workflowPath = summary ? `/workflows/${summary.id}/summary` : undefined;
  const publicTitle = summary?.public_title?.trim();
  const publicDescription = summary?.public_description?.trim();

  const handleRun = async () => {
    if (!selectedWorkflowId) return;
    try {
      const result = await runWorkflow.mutateAsync({
        employeeId: employeeId,
        workflowId: selectedWorkflowId,
      });

      analyticsService.trackEvent(analyticEvents.workflows.assignedToEmployee, {
        [analyticProperties.id]: employeeId,
      });

      if (result.survey) {
        close(id);
        navigate(`/surveys/${result.survey.survey_id}/respond`);
        return;
      }

      showInfo({
        title: t("workflow:assign_workflow_success"),
      });

      close(id);
    } catch {
      return;
    }
  };

  const handleOpenWorkflow = () => {
    if (!workflowPath) return;
    window.open(workflowPath, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal
      title={t("workflow:assign_workflow")}
      variant={"middle"}
      layout={"base"}
      size={"md"}
      withCloser={true}
      actions={
        <Stack gap="smd">
          <Button isLoading={runWorkflow.isPending} onClick={handleRun} size={"lg"}>
            {t("workflow:run_workflow")}
          </Button>
          <Button size="md" variant="plain" onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
      {...props}
    >
      <>
        <Controller
          control={control}
          name="workflowId"
          render={({ field }) => (
            <Select
              label={t("workflow:choose_workflow")}
              labelWhenSelected={t("workflow:workflow")}
              emptyTitle={t("workflow:choose_workflow")}
              options={selectOptions}
              searchOptions={selectOptions}
              onSearchOptions={(text) => setSearchTerm(text ?? "")}
              value={field.value}
              onChange={(value) => {
                const id = typeof value === "number" ? value : undefined;
                field.onChange(id);
                setSelectedWorkflowId(id);
              }}
              searchable
              showCaption
              isClearable
              isLoading={isFetching}
            />
          )}
        />

        {selectedWorkflowId && (
          <Stack className={"pt-4"} gap={"lg"}>
            {isSummaryLoading && <Skeleton className={"h-40 w-full"} />}
            {summary && !isSummaryLoading && (
              <Stack className={"rounded-xs border border-dark/5 bg-dark/5 p-5"} gap={"xl"}>
                {publicTitle && (
                  <>
                    <Stack gap={"sm"}>
                      <Text className={"text-xs uppercase text-default"}>
                        {t("workflow:public_title_label")}
                      </Text>
                      <WorkflowSummaryRow
                        leading={<Avatar emoji={summary.emoji} />}
                        title={publicTitle}
                        caption={publicDescription}
                      />
                    </Stack>
                  </>
                )}

                <Stack gap={"md"}>
                  <Text size="xs" className="uppercase">
                    {t("workflow:workflow_details_label")}
                  </Text>

                  {summary.description && (
                    <>
                      <WorkflowSummaryRow
                        leading={
                          <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-circle-info"} />} />
                        }
                        title={t("common:description")}
                        caption={summary.description}
                      />
                      <hr className={"border-dark/10"} />
                    </>
                  )}

                  <WorkflowSummaryRow
                    leading={getWorkflowTriggerTypeIcon(summary.trigger_type)}
                    title={getApiWorkflowTriggerTypeLabel(summary.trigger_type)}
                    captionSlot={
                      selectedWorkflow && (
                        <WorkflowTriggerCaption
                          workflow={selectedWorkflow}
                          triggerDescription={getWorkflowTriggerDescription(selectedWorkflow)}
                        />
                      )
                    }
                  />
                  <hr className={"border-dark/10"} />

                  <WorkflowSummaryRow
                    leading={
                      <Avatar
                        url={getEmployeeAvatarUrl(summary.owner, "md")}
                        acronym={getNameInitials(renderEmployeeName(summary.owner))}
                      />
                    }
                    title={renderEmployeeName(summary.owner)}
                    caption={
                      summary.datetime_created
                        ? t("workflow:created_ago", {
                            time: renderTimeAgo(summary.datetime_created),
                          })
                        : undefined
                    }
                  />
                  <hr className={"border-dark/10"} />

                  {canOpenWorkflow && (
                    <Box>
                      <Button
                        variant={"secondary"}
                        size={"md"}
                        onClick={handleOpenWorkflow}
                        rightIcon={
                          <FontAwesomeIcon icon={"fa-light fa-arrow-up-right-from-square"} />
                        }
                      >
                        {t("workflow:view_or_edit_workflow")}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </>
    </Modal>
  );
}
